import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fetchMe, api } from "@/lib/auth";
import {
  initialState,
  REQUIRED_TEXT_FIELDS,
} from "@/components/couple-signup/constants";
import { CoupleSignupForm } from "@/components/couple-signup/CoupleSignupForm";
import { devLog } from "@/lib/log";

const LOGO_URL = "/logo-transparent.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoupleSignup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLastSavedAt, setDraftLastSavedAt] = useState(null);

  // Auth-gated. Pre-fill the email from the signed-in user so the signup
  // is guaranteed to map back to their account on next sign-in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      if (!me) {
        navigate("/signin?next=/couple/signup", { replace: true });
        return;
      }
      if (me.role === "admin") {
        navigate("/admin", { replace: true });
        return;
      }
      // If this account already submitted a couple form, bypass it.
      // If they're a vendor, route them to their own form instead.
      try {
        const { data: status } = await api.get("/auth/my-status");
        if (cancelled) return;
        if (status.has_couple_application) {
          navigate("/", { replace: true });
          return;
        }
        if (status.role === "vendor" || status.has_vendor_application) {
          navigate("/vendor/apply", { replace: true });
          return;
        }
      } catch (e) {
        devLog("ld_couple_signup post-submit best-effort failed:", e?.message);
      }
      setForm((s) => ({ ...s, email: me.email || s.email }));
      // Load any previously-saved draft so the couple picks up where they left off.
      try {
        const { data: draft } = await api.get("/couple-signups/draft");
        if (cancelled) return;
        if (draft && Object.keys(draft).length > 0) {
          setForm((prev) => {
            const next = { ...prev };
            Object.keys(initialState).forEach((k) => {
              if (draft[k] !== undefined && draft[k] !== null) next[k] = draft[k];
            });
            next.email = me.email || next.email;
            return next;
          });
          if (draft.updated_at) setDraftLastSavedAt(draft.updated_at);
        }
      } catch (e) {
        devLog("ld_couple_signup draft load failed:", e?.message);
      }
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftPayload = {
        ...form,
        declaration_confirmed: !!form.declaration_confirmed,
      };
      const { data } = await api.put("/couple-signups/draft", draftPayload);
      setDraftLastSavedAt(data?.updated_at || new Date().toISOString());
      toast("Draft saved", {
        description: "You can return here any time to finish it.",
        duration: 2600,
      });
    } catch (err) {
      toast("Couldn't save draft", {
        description: err?.response?.data?.detail || "Please try again.",
        duration: 3000,
      });
    } finally {
      setSavingDraft(false);
    }
  };

  const validate = () => {
    for (const [k, label] of REQUIRED_TEXT_FIELDS) {
      if (!String(form[k]).trim()) {
        toast(`Please complete: ${label}`, { duration: 2800 });
        return false;
      }
    }
    if (form.wedding_type.length === 0) {
      toast("Please tell us what your wedding looks like", { duration: 2800 });
      return false;
    }
    if (form.services_needed.length === 0) {
      toast("Please select at least one service you need", {
        duration: 2800,
      });
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast("Please enter a valid email address", { duration: 2800 });
      return false;
    }
    const guests = Number(form.guest_count);
    if (!Number.isFinite(guests) || guests <= 0) {
      toast("Please enter a valid guest count", { duration: 2800 });
      return false;
    }
    // Consultation slot is compulsory. If no slots are open the SlotPicker
    // itself shows the "Please contact Lux." message — either way the form
    // can't be submitted until a date + time is picked.
    if (!form.intro_slot_date || !form.intro_slot_time) {
      toast("Please select an introduction slot before submitting", {
        description:
          "If no slots are showing, please contact Lux to arrange a time.",
        duration: 3400,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: Number(form.guest_count),
      };
      await axios.post(`${API}/couple-signups`, payload);
      setSubmitted(true);
      setForm(initialState);
      try {
        await api.delete("/couple-signups/draft");
      } catch (e) {
        devLog("ld_couple_signup draft cleanup failed:", e?.message);
      }
      try {
        localStorage.setItem("ld_member", "couple");
      } catch (e) {
        devLog("ld_couple_signup storage failed:", e?.message);
      }
      toast("Thank you — we've received your details", {
        description:
          "We'll review and start matching you with the right vendors.",
        duration: 3500,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast("Couldn't submit. Please try again.", {
        description: err?.response?.data?.detail || "Network error.",
        duration: 3500,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="couple-signup-page"
      className="relative min-h-[100dvh] w-full overflow-x-hidden"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      {!authChecked && (
        <div className="absolute inset-0 flex items-center justify-center text-[var(--ld-ink-muted)] text-sm">
          Loading…
        </div>
      )}
      {authChecked && (
      <>
      {/* Top bar */}
      <header className="relative z-20 flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7">
        <Link
          to="/"
          data-testid="back-login"
          className="inline-flex items-center gap-2 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          <span className="font-accent text-[10px] tracking-[0.25em]">
            Back
          </span>
        </Link>
      </header>

      <section className="px-6 md:px-12 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden
              className="mx-auto w-[100px] h-auto"
            />
            <span className="mt-6 inline-block font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Couple Onboarding
            </span>
            <h1
              data-testid="couple-heading"
              className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[var(--ld-ink)] font-medium"
            >
              Tell us about your big day.
            </h1>
            <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)] max-w-xl mx-auto">
              Please complete the questions to the best of your ability so
              that we are able to suggest the appropriate vendors for your big
              day. Required fields are marked with{" "}
              <span className="text-[var(--ld-gold-deep)]">*</span>.
            </p>
            <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
          </div>

          {submitted && (
            <div
              data-testid="submitted-banner"
              className="mt-10 border border-[var(--ld-gold)]/60 bg-[var(--ld-ivory-warm)] p-6 text-center"
            >
              <h2 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Thank you — we&apos;ve received your details.
              </h2>
              <p className="mt-2 text-sm text-[var(--ld-ink-muted)]">
                Our team will review your wedding plans and start curating
                vendor suggestions tailored to you.
              </p>
            </div>
          )}

          <CoupleSignupForm
            form={form}
            setForm={setForm}
            set={set}
            onSubmit={handleSubmit}
            submitting={submitting}
            onSaveDraft={handleSaveDraft}
            savingDraft={savingDraft}
            draftLastSavedAt={draftLastSavedAt}
          />
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
        © {new Date().getFullYear()} · Luxé Directory
      </footer>
      </>
      )}
    </main>
  );
};

export default CoupleSignup;
